import { useState, useRef } from "react";
import { CheckCircle2, Upload, X, FileText } from "lucide-react";
import { Button } from "@components/common/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/common/dialog";
import { Label } from "@components/common/label";
import { cn, getFinancialErrorMessage } from "@pma/utils/helpers";
import useTranslator from "@pma/hooks/use-translator";
import { formatCurrency } from "@pma/utils/utils";
import { DictType } from "@pma/types";
import axios from "axios";
import { FINANCIAL_SERVER_URL, REQUEST_SIGNING_ENDPOINT } from "@pma/utils/api-routes";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { parseCookies } from "nookies";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@pma/hooks/use-toast";

interface Props {
  requestItem: DictType;
  revalidate: CallableFunction;
}

export function ItemRequestSigningDialog({ requestItem, revalidate }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [signature, setSignature] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { translate, locale } = useTranslator();

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const isHebrew = locale === "he";

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files: FileList) => {
    const newFiles = Array.from(files).filter(
      (file) => file.type.startsWith("image/") || file.type === "application/pdf",
    );

    if (newFiles.length > 0) {
      setSignature((prev) => [...prev, ...newFiles]);
    }
  };

  const removeAttachment = (index: number) => {
    setSignature((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setSignature([]);
  };

  const handleCancel = () => {
    resetForm();
  };

  const renderPreview = (file: File, index: number) => {
    const isImage = file.type.startsWith("image/");

    return (
      <div key={`${file.name}-${index}`} className='relative  w-full'>
        <div className='border rounded-md overflow-hidden bg-muted/30 relative group-hover:opacity-80 transition-opacity'>
          {isImage ? (
            <div className='relative w-full'>
              <img
                src={URL.createObjectURL(file) || "/placeholder.svg"}
                alt={file.name}
                className='h-full w-full object-cover'
              />
            </div>
          ) : (
            <div className='h-24 w-24 flex items-center justify-center'>
              <FileText className='h-10 w-10 text-muted-foreground' />
            </div>
          )}
        </div>
        <div className='flex w-full justify-between'>
          <p className='text-xs mt-1 truncate max-w-[96px]'>{file.name}</p>
          <Button
            type='button'
            variant={"link"}
            onClick={() => removeAttachment(index)}
            className='text-destructive flex items-center justify-center'
            aria-label='Remove attachment'
          >
            {translate("Replace")}
            <X className='h-3 w-3' />
          </Button>
        </div>
      </div>
    );
  };

  const signItemRequest = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + REQUEST_SIGNING_ENDPOINT,
      {
        item_changes_id: requestItem?.id,
        sign: signature[0],
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  };
  const handleRequestItemSigningMutation = useMutation({
    mutationKey: ["sign-request-item"],
    mutationFn: signItemRequest,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("item signed successfully"),
        variant: "success",
      });
      resetForm();
      revalidate();
      setIsOpen(false);
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "sign item", translate),
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button variant='outline' size='sm'>
          <FileText className='h-4 w-4 mr-1' />
          {translate("Sign")}
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[650px] max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{translate("Sign Requested Item")}</DialogTitle>
          <DialogDescription>
            {translate("Sign item")} <strong>#{requestItem?.new_item_data?.number}</strong>{" "}
            {translate("of approved amount")}{" "}
            <strong>{formatCurrency(requestItem?.approved_amount, isHebrew)}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className='grid gap-6 py-4'>
          <div className='grid gap-2'>
            <Label>{translate("Signature")}</Label>
            {signature.length < 1 ? (
              <div
                className={cn(
                  "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                  isDragging
                    ? "border-primary bg-primary/5"
                    : "border-muted-foreground/25 hover:border-primary/50",
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  type='file'
                  ref={fileInputRef}
                  onChange={handleFileInputChange}
                  accept='image/*,application/pdf'
                  className='hidden'
                  multiple
                />
                <div className='flex flex-col items-center gap-2'>
                  <div className='h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center'>
                    <Upload className='h-5 w-5 text-primary' />
                  </div>
                  <div className='flex flex-col space-y-1'>
                    <p className='text-sm font-medium'>
                      {isDragging ? translate("Drop files here") : translate("Upload signature")}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      {translate("Drag and drop image or click to browse")}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className='mt-4'>
                <Label className='mb-2 block'>{translate("Uploaded File")}</Label>
                <div className='flex  gap-4'>
                  {signature?.map((file, index) => renderPreview(file, index))}
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button size={"sm"} variant='outline' onClick={handleCancel}>
            {translate("Cancel")}
          </Button>
          <Button
            size={"sm"}
            disabled={signature?.length < 1}
            onClick={() => handleRequestItemSigningMutation.mutate()}
            className='gap-1'
            loading={handleRequestItemSigningMutation.isPending}
          >
            <CheckCircle2 className='h-4 w-4' />
            {translate("Sign Item Request")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
